import { MAX_SANE_NUMBER_OF_OWNERS } from '@components/Apply/OwnersMoreDetailsAboutYou/getNumberOfOwners';
import { isSyntheticTest } from '@utils/platformBasedInfo';
import { recordApplyEvent } from '@utils/recordEvent';
import { EntireDealFormattedResponse } from 'src/js/types';

const padZero = (value: number): string => {
  return value < 10 ? `0${value}` : `${value}`;
};

export const addOwnersToPost = ({
  post,
  form,
  type = 'standard',
  isNotAnOwner = false,
}: {
  post: EntireDealFormattedResponse;
  form: any;
  type?: 'standard' | 'eqlead';
  isNotAnOwner?: boolean;
}): EntireDealFormattedResponse => {
  const newPost = { ...post };

  const maxOwners = MAX_SANE_NUMBER_OF_OWNERS;

  if (!newPost.business) {
    newPost.business = {};
  }

  if (!newPost.business.contacts) {
    newPost.business.contacts = [];
  }

  // Track each form event once per submission, not once per owner: the loop
  // below runs for every owner, and unguarded tracking fired 2-3 identical
  // Segment events within milliseconds for multi-owner applications.
  let trackedOwnersInfo = false;
  let trackedOwnersOwnership = false;
  let trackedOwnersIdentity = false;

  for (let idx = isNotAnOwner ? 0 : 1; idx <= maxOwners; idx++) {
    if (type === 'standard') {
      if (form[`owner_${idx}_id`]) {
        if (!newPost.business.contacts[isNotAnOwner ? idx : idx - 1])
          newPost.business.contacts[isNotAnOwner ? idx : idx - 1] = {};
        newPost.business.contacts[isNotAnOwner ? idx : idx - 1].id = form[`owner_${idx}_id`];
      }
    } else {
      if (form[`owner_${idx}_lead_id`]) {
        if (!newPost.business.contacts[isNotAnOwner ? idx : idx - 1])
          newPost.business.contacts[isNotAnOwner ? idx : idx - 1] = {};
        newPost.business.contacts[isNotAnOwner ? idx : idx - 1].id = form[`owner_${idx}_lead_id`];
      }
    }
    if (form[`owner_${idx}_first`]) {
      if (!newPost.business.contacts[isNotAnOwner ? idx : idx - 1])
        newPost.business.contacts[isNotAnOwner ? idx : idx - 1] = {};
      newPost.business.contacts[isNotAnOwner ? idx : idx - 1].firstName = form[`owner_${idx}_first`];
      if (!trackedOwnersInfo) {
        trackedOwnersInfo = true;
        (async () => {
          if (!isSyntheticTest) {
            analytics.track('Apply Form - Owners Info', {
              firstName: newPost.business?.contacts?.[isNotAnOwner ? idx : idx - 1].firstName,
            });
            recordApplyEvent('Apply Form - Owners Info', {
              firstName: newPost.business?.contacts?.[isNotAnOwner ? idx : idx - 1].firstName,
            });
          }
        })();
      }
    }
    if (form[`owner_${idx}_last`]) {
      if (!newPost.business.contacts[isNotAnOwner ? idx : idx - 1])
        newPost.business.contacts[isNotAnOwner ? idx : idx - 1] = {};
      newPost.business.contacts[isNotAnOwner ? idx : idx - 1].lastName = form[`owner_${idx}_last`];
    }
    if (form[`owner_${idx}_mobile`]) {
      if (!newPost.business.contacts[isNotAnOwner ? idx : idx - 1])
        newPost.business.contacts[isNotAnOwner ? idx : idx - 1] = {};
      newPost.business.contacts[isNotAnOwner ? idx : idx - 1].phoneMobile = form[`owner_${idx}_mobile`];
    }
    if (form[`owner_${idx}_email`]) {
      if (!newPost.business.contacts[isNotAnOwner ? idx : idx - 1])
        newPost.business.contacts[isNotAnOwner ? idx : idx - 1] = {};
      newPost.business.contacts[isNotAnOwner ? idx : idx - 1].email = form[`owner_${idx}_email`];
    }
    if (form[`owner_${idx}_dob`]) {
      if (!newPost.business.contacts[isNotAnOwner ? idx : idx - 1])
        newPost.business.contacts[isNotAnOwner ? idx : idx - 1] = {};
      newPost.business.contacts[isNotAnOwner ? idx : idx - 1].dob = form[`owner_${idx}_dob`];
    }

    if (form[`owner_${idx}_dob_day`] && form[`owner_${idx}_dob_month`] && form[`owner_${idx}_dob_year`]) {
      if (!newPost.business.contacts[isNotAnOwner ? idx : idx - 1])
        newPost.business.contacts[isNotAnOwner ? idx : idx - 1] = {};
      const day = padZero(form[`owner_${idx}_dob_day`]);
      const month = padZero(form[`owner_${idx}_dob_month`]);
      const year = form[`owner_${idx}_dob_year`];
      const date = new Date(`${year}-${month}-${day}T00:00:00.000Z`);
      newPost.business.contacts[isNotAnOwner ? idx : idx - 1].dob = date.toISOString();
    }

    if (form[`owner_${idx}_title`]) {
      if (!newPost.business.contacts[isNotAnOwner ? idx : idx - 1])
        newPost.business.contacts[isNotAnOwner ? idx : idx - 1] = {};
      newPost.business.contacts[isNotAnOwner ? idx : idx - 1].title = form[`owner_${idx}_title`];
    }

    if (form[`owner_${idx}_ownership`]) {
      if (!newPost.business.contacts[isNotAnOwner ? idx : idx - 1])
        newPost.business.contacts[isNotAnOwner ? idx : idx - 1] = {};
      newPost.business.contacts[isNotAnOwner ? idx : idx - 1].ownerPercentage = Number(form[`owner_${idx}_ownership`]);
      if (!trackedOwnersOwnership) {
        trackedOwnersOwnership = true;
        (async () => {
          if (!isSyntheticTest) {
            // Property was historically mislabeled `firstName` while holding
            // the ownership percentage — renamed to what it actually is.
            analytics.track('Apply Form - Owners Ownership', {
              ownerPercentage: newPost.business?.contacts?.[isNotAnOwner ? idx : idx - 1].ownerPercentage,
            });
            recordApplyEvent('Apply Form - Owners Ownership', {
              ownerPercentage: newPost.business?.contacts?.[isNotAnOwner ? idx : idx - 1].ownerPercentage,
            });
          }
        })();
      }
    }

    if (idx === 1 && form.business_street && form.home_based === 'true') {
      newPost.business.contacts[isNotAnOwner ? idx : idx - 1].home_based = true;
      newPost.business.contacts[isNotAnOwner ? idx : idx - 1] = {
        ...newPost.business.contacts[isNotAnOwner ? idx : idx - 1],
        address: {
          address1: form.business_street,
          address2: form.business_suite,
          city: form.business_city,
          state: form.business_state,
          postalCode: form.business_zip,
        },
      };
      (async () => {
        if (!isSyntheticTest) {
          analytics.track('Apply Form - Owners Address', {
            ...newPost?.business?.contacts?.[isNotAnOwner ? idx : idx - 1],
          });
          recordApplyEvent('Apply Form - Owners Address', {
            ...newPost?.business?.contacts?.[isNotAnOwner ? idx : idx - 1],
          });
        }
      })();
    } else if (form[`owner_${idx}_street`]) {
      newPost.business.contacts[isNotAnOwner ? idx : idx - 1] = {
        ...newPost.business.contacts[isNotAnOwner ? idx : idx - 1],
        address: {
          address1: form[`owner_${idx}_street`],
          address2: form[`owner_${idx}_suite`],
          city: form[`owner_${idx}_city`],
          state: form[`owner_${idx}_state`],
          postalCode: form[`owner_${idx}_zip`],
        },
      };
    }

    if (form[`owner_${idx}_ssn`]) {
      newPost.business.contacts[isNotAnOwner ? idx : idx - 1].ssn = form[`owner_${idx}_ssn`];
      if (!trackedOwnersIdentity) {
        trackedOwnersIdentity = true;
        (async () => {
          if (!isSyntheticTest) {
            analytics.track('Apply Form - Owners Identity', {
              ssn: 'removed PI',
            });
            recordApplyEvent('Apply Form - Owners Identity', {
              ssn: 'removed PI',
            });
          }
        })();
      }
    }

    if (form[`owner_${idx}_dob`]) {
      newPost.business.contacts[isNotAnOwner ? idx : idx - 1].dob = form[`owner_${idx}_dob`];
    }
    if (idx === 0 && isNotAnOwner) {
      console.log('Setting isSubmitter for contact at index 0');
      console.log('Values so far:', newPost.business.contacts[0]);
      if (Object.keys(newPost.business.contacts[0]).length > 0) {
        newPost.business.contacts[0].isSubmitter = true;
      }
    }
  }
  return newPost;
};
